import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const initialState = {
  campaigns: [],
  status: "idle",
  error: null,
};

export const fetchCampaigns = createAsyncThunk(
  "campaign/fetchCampaigns",
  async () => {
    try {
      const response = await fetch("https://adbot.nw18.com/api/campaigns");
      if (!response.ok) {
        throw new Error("Failed to fetch campaigns");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      throw new Error("Failed to fetch campaigns");
    }
  }
);

export const deleteCampaign = createAsyncThunk(
  "campaign/deleteCampaign",
  async (campaignId) => {
    try {
      const response = await fetch(`https://adbot.nw18.com/api/deleteCampaign/${campaignId}`, {
        method: "DELETE"
      });

      if (!response.ok) {
        throw new Error("Failed to delete campaign");
      }
    } catch (error) {
      throw new Error("Failed to delete campaign");
    }
  }
);

export const addDashboardToCampaign = createAsyncThunk(
  "campaign/addDashboardToCampaign",
  async ({ campaignId, dashboardData }) => {
    try {
      const response = await fetch(`https://adbot.nw18.com/api/updateCampaign/${campaignId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dashboardData),
      });

      if (!response.ok) {
        throw new Error("Failed to add dashboard to campaign");
      }

      const updatedCampaignData = await response.json();
      return { campaignId, dashboardData: updatedCampaignData };
    } catch (error) {
      throw new Error("Failed to add dashboard to campaign");
    }
  }
);
export const addCampaignAsync = createAsyncThunk(
  "campaign/addCampaignAsync",
  async (newCampaignData) => {
    try {
      console.log(newCampaignData);
      
      const response = await fetch('https://adbot.nw18.com/api/addCampaign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newCampaignData),
      });
      if (!response.ok) {
        throw new Error('Failed to add campaign');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      throw new Error('Failed to add campaign');
    }
  }
);

export const updateCampaignAsync = createAsyncThunk(
  "campaign/updateCampaignAsync",
  async ({ campaignId, updatedCampaignData }) => {
    try {
      const response = await fetch(`https://adbot.nw18.com/api/updateCampaign/${campaignId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedCampaignData),
      });
      if (!response.ok) {
        throw new Error('Failed to update campaign');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      throw new Error('Failed to update campaign');
    }
  }
);

export const campaignSlice = createSlice({
  name: "campaign",
  initialState,
  reducers: {
    addCampaign: (state, action) => {
      state.campaigns.push({ ...action.payload, id: state.campaigns.length + 1 });
    },
    removeCampaign: (state, action) => {
      state.campaigns = state.campaigns.filter(
        (campaign) => campaign.id !== action.payload
      );
    },
    updateCampaignStatus: (state, action) => {
      const campaign = state.campaigns.find(
        (campaign) => campaign.id === action.payload.campaignId
      );
      if (campaign) {
        campaign.status = action.payload.newStatus;
      }
    },
    updateCampaignDetails: (state, action) => {
      const { id, name, subDomain, fallBackUrl, systemMessage, leadQuestionNumber, leadFields } = action.payload;
      const campaign = state.campaigns.find((campaign) => campaign.id === id);
      if (campaign) {
        campaign.name = name;
        campaign.subDomain = subDomain;
        campaign.fallBackUrl = fallBackUrl;
        campaign.systemMessage = systemMessage;
        campaign.body.leadQuestionNumber = leadQuestionNumber;
        campaign.body.leadFields = leadFields;
      }
    },

    updateAzureOpenAIConfig: (state, action) => {
      const { campaignId, azureOpenAI, systemMessage } = action.payload;
      const campaign = state.campaigns.find(c => c.id === campaignId);
      if (campaign) {
        campaign.azureOpenAI = azureOpenAI;
        campaign.systemMessage = systemMessage;
      }
    },
    
    addDashboardToCampaignSuccess: (state, action) => {
      const { campaignId, dashboardData } = action.payload;
      const campaign = state.campaigns.find((campaign) => campaign.id === campaignId);
      if (campaign) {
        campaign.dashboards.push({ ...dashboardData, status: "draft" });
      }
    },
  },
  
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.campaigns = action.payload;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(addDashboardToCampaign.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addDashboardToCampaign.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { campaignId, dashboardData } = action.payload;
        const campaign = state.campaigns.find((campaign) => campaign.id === campaignId);
        if (campaign) {
          campaign.dashboards = dashboardData; 
        }
      })
      .addCase(addDashboardToCampaign.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(updateCampaignAsync.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updatedCampaign = action.payload;
        const index = state.campaigns.findIndex(campaign => campaign.id === updatedCampaign.id);
        if (index !== -1) {
          state.campaigns[index] = updatedCampaign;
        }
      });
  },
});

export const { addCampaign, removeCampaign, updateCampaignStatus, updateCampaignDetails, addDashboardToCampaignSuccess, updateAzureOpenAIConfig } =
  campaignSlice.actions;
  
  export const selectCampaigns = (state) => state.campaign.campaigns;

export default campaignSlice.reducer;
